// Home-overview data layer (#321 shipped): the account-wide top-N reads —
//   GET /v1/sessions   — recent agent sessions, newest-first (`read:sessions`)
//   GET /v1/approvals  — PRs parked on `ask_before_acting`, longest-waiting first
// plus the pure derivations the stat band + fleet table render. Both reads are
// LIVE (`createLiveQuery`): a change on the `sessions` / `approvals` topic
// re-reads them, and their 30 s poll runs only while the stream does not vouch.

import type { MergeAskCauseKind } from '@developerz/domain';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { UtilBox, UtilJob } from './fleetUtilization';
import { createLiveQuery, type LiveQuery } from './live/live-query';
import { runningJobs } from './machine-jobs';
import type { RepoRow } from './repos';
import { SESSION_TOKEN } from './session';

/** One recent agent session — the `/v1/sessions` projection. */
export type AccountSession = {
  readonly id: string;
  readonly repo_full_name: string;
  readonly trigger: string;
  readonly entity_kind: string;
  /** GitHub number of the triggering issue/PR (#853); null when unresolved. */
  readonly entity_number: number | null;
  /** Human-readable label — issue title, `PR #<n>`, or the raw trigger (#853). */
  readonly entity_title: string;
  readonly outcome: string | null;
  readonly started_iso: string;
  readonly finished_iso: string | null;
};

/** One PR awaiting a human merge decision — the `/v1/approvals` projection. */
export type ApprovalRow = {
  readonly id: string;
  readonly repo_full_name: string;
  readonly github_number: number;
  /** Human PR title (#852); null when the platform never saw one. */
  readonly title: string | null;
  readonly author_login: string | null;
  readonly ci_status: string;
  readonly last_session_id: string | null;
  readonly opened_iso: string;
  /**
   * The PR head this ask is about — sent back with the decision (plan 101 M2).
   * Null until a head is seen; such a row cannot be decided yet.
   */
  readonly head_sha: string | null;
  /**
   * Why the row is back on `ask` (#5671) — the domain's `MergeAskCauseKind`,
   * null when nothing is recorded. Only `stale_approval` renders a note.
   */
  readonly merge_ask_cause: MergeAskCauseKind | null;
  /** The head the voided approval covered; null when nothing is recorded. */
  readonly merge_ask_prior_head_sha: string | null;
};

export const SESSIONS_QUERY_KEY = 'account_sessions';
export const APPROVALS_QUERY_KEY = 'account_approvals';

/**
 * Bounded top-N (the endpoints are tiles, not archives — `next_cursor` is always null).
 * Also the completeness floor of the live-work count ({@link inFlightCount}): the
 * `sessions` stream topic's snapshot cap (`STREAM_SNAPSHOT_LIMIT`, api `streams.ts`) is
 * larger, so fewer rows than this is under BOTH caps — the account's whole history.
 */
export const SESSIONS_LIMIT = 50;
const LIMIT = SESSIONS_LIMIT;

/**
 * The approval queue's head: the route's own ceiling (`MAX_LIMIT`,
 * `apps/api/src/routes/overview.ts`), longest-waiting first. It mints no cursor,
 * so rows FILLING it, or trailing the exact `pending_approvals` count, is the
 * only sign of a truncation — the screen names it (#5608 slice 17 row 14).
 */
export const APPROVALS_LIMIT = 100;

/**
 * Recent account-wide agent sessions, newest-first, kept fresh by the `sessions`
 * topic. Idle with no session. The whole {@link LiveQuery}, for a screen that
 * shows the connection (Home's audit pill); {@link useAccountSessions} is the read.
 */
export function useLiveAccountSessions(
  token: string | null = SESSION_TOKEN,
): LiveQuery<readonly AccountSession[]> {
  return createLiveQuery<readonly AccountSession[]>(
    () => ({
      queryKey: [SESSIONS_QUERY_KEY, token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'session' }],
      queryFn: async () =>
        (await apiFetch<{ data: readonly AccountSession[] }>(`/v1/sessions?limit=${LIMIT}`, token))
          .data,
    }),
    token,
  );
}

/** Recent account-wide agent sessions, newest-first, live. Idle with no session. */
export function useAccountSessions(token: string | null = SESSION_TOKEN) {
  return useLiveAccountSessions(token).query;
}

/**
 * Pending `ask_before_acting` approvals, longest-waiting first, kept fresh by the
 * `approvals` topic. Idle with no session.
 */
export function useApprovals(token: string | null = SESSION_TOKEN) {
  return createLiveQuery<readonly ApprovalRow[]>(
    () => ({
      queryKey: [APPROVALS_QUERY_KEY, token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'approval' }],
      queryFn: async () =>
        (
          await apiFetch<{ data: readonly ApprovalRow[] }>(
            `/v1/approvals?limit=${APPROVALS_LIMIT}`,
            token,
          )
        ).data,
    }),
    token,
  ).query;
}

/** Stat-band count voice: zero-pad to 2 digits under 10 ("03"). */
export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Sessions still running (no finish timestamp). */
export function runningCount(sessions: readonly AccountSession[]): number {
  return sessions.filter((s) => s.finished_iso === null).length;
}

/**
 * The boxes tile's live work: the unfinished sessions among `rows` (the live
 * `/v1/sessions` read), and whether that count is the account's whole truth. No route counts
 * in-flight sessions account-wide (`/v1/overview/counts` does not; `load.live_sessions`
 * counts box-held tasks plus the account's active CI jobs), and both session reads are a
 * newest-N slice, so:
 *
 * - `complete` only when `rows` holds FEWER than {@link SESSIONS_LIMIT}: under both caps,
 *   so it is every session the account has. A full window can hide an older session
 *   still running behind 50 newer finished ones, so its count is a FLOOR. "The oldest
 *   loaded row is finished" proves nothing — a long run started before it still runs.
 * - undefined before the read answers.
 */
export type LiveWork = { readonly count: number; readonly complete: boolean };

export function inFlightCount(rows: readonly AccountSession[] | undefined): LiveWork | undefined {
  if (rows === undefined) return undefined;
  return { count: runningCount(rows), complete: rows.length < SESSIONS_LIMIT };
}

/** The longest-waiting pending approval's open timestamp, or null when clear. */
export function oldestApprovalIso(approvals: readonly ApprovalRow[]): string | null {
  return approvals.reduce<string | null>(
    (oldest, a) => (oldest === null || a.opened_iso < oldest ? a.opened_iso : oldest),
    null,
  );
}

/**
 * One Agent-fleet table row — a repo joined to the box run it holds, else its latest
 * recent session.
 *
 * `working` is BOX-HELD work only: one of a box's RUNNING jobs (`assigned` /
 * `in_progress`, #6745) names this repo; a paused job runs nothing. A session in
 * flight with no box is `active` (2026-10-30 owner report): agent sessions run worker-side
 * (reviews, triage, babysit, ~15 s) and hold no box, so calling them "Working" beside a
 * "Boxes working 0/21" tile said one word with two meanings. The table words an `active`
 * row by its `trigger` (`FleetTable`).
 */
export type FleetRow = {
  readonly repo_full_name: string;
  /** Latest session trigger (`issue`/`pr`/`release`/`manual`), or null when none. */
  readonly trigger: string | null;
  /** The box-held task's title on a `working` row, else null. */
  readonly task: string | null;
  readonly status: 'working' | 'active' | 'idle' | 'offline';
  readonly updated_iso: string | null;
};

/** Live work first (box-held, then worker-side), then most-recent activity; never-seen repos last. */
const STATUS_RANK: Record<FleetRow['status'], number> = {
  working: 0,
  active: 1,
  idle: 2,
  offline: 3,
};

/**
 * Most-recently-active first. A running session outranks a finished one even if
 * it started earlier: `updated_iso` on a live row is its START, so pure
 * recency buries the long-running session the reader came here to find. Ties
 * (and the offline tail, whose `updated_iso` is null) fall back to repo name so
 * the order is stable across polls.
 */
export function byFleetActivity(a: FleetRow, b: FleetRow): number {
  return (
    STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
    (b.updated_iso ?? '').localeCompare(a.updated_iso ?? '') ||
    a.repo_full_name.localeCompare(b.repo_full_name)
  );
}

/** Repo → the first running job a box holds for it (any job of any box, #6745). */
function boxHeldByRepo(boxes: readonly Pick<UtilBox, 'jobs'>[]): Map<string, UtilJob> {
  const held = new Map<string, UtilJob>();
  for (const b of boxes) {
    for (const job of runningJobs(b)) {
      if (job.repo && !held.has(job.repo)) held.set(job.repo, job);
    }
  }
  return held;
}

/**
 * Join the repo list to the box roster and the recent-sessions slice: a box holding a
 * run for the repo → working, a running session → active, a finished one → idle, none
 * in the recent window → offline. `boxes` is empty until the utilization read answers
 * (or when it is refused), so no row is called working on a guess. Sorted by
 * `byFleetActivity` — the panel pages a few rows at a time, so repo-list order put the
 * repo you are watching on page three.
 */
export function toFleetRows(
  repos: readonly RepoRow[],
  sessions: readonly AccountSession[],
  boxes: readonly Pick<UtilBox, 'jobs'>[] = [],
): readonly FleetRow[] {
  const held = boxHeldByRepo(boxes);
  const rows = repos.map((repo): FleetRow => {
    // Sessions arrive newest-first, so the first hit is the latest.
    const latest = sessions.find((s) => s.repo_full_name === repo.full_name);
    const trigger = latest?.trigger ?? null;
    const task = held.get(repo.full_name);
    if (task) {
      return {
        repo_full_name: repo.full_name,
        trigger,
        task: task.title,
        status: 'working',
        updated_iso: task.started_at ?? latest?.started_iso ?? null,
      };
    }
    if (!latest) {
      return {
        repo_full_name: repo.full_name,
        trigger: null,
        task: null,
        status: 'offline',
        updated_iso: null,
      };
    }
    return {
      repo_full_name: repo.full_name,
      trigger,
      task: null,
      status: latest.finished_iso === null ? 'active' : 'idle',
      updated_iso: latest.finished_iso ?? latest.started_iso,
    };
  });
  return rows.sort(byFleetActivity);
}
